import {
    useCallback,
    useEffect,
    useState
} from "react";
import {
    ActivityIndicator,
    View
} from "react-native";
import type MailsListProps from "./type";
import stylesheet from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme,
    TextInput,
    Button,
    Text
} from "ncore-ui-kit";
import {
    type SentMailItem
} from "../../../../services/restAPI/actions/getAllSentMails/type";
import {
    PlusIcon,
    EyeIcon
} from "lucide-react-native";
import moment from "moment";
import RESTServiceContext from "../../../../services/restAPI/context";
import Pagination from "../../../../components/pagination";
import MailDetailModal from "../mailDetailModal";

const MailsList = ({
    onOpenSendModal
}: MailsListProps) => {
    const {
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext();

    const {
        localize
    } = NCoreUIKitLocalize.useContext();

    const [
        mails,
        setMails
    ] = useState<SentMailItem[]>([]);

    const [
        totalCount,
        setTotalCount
    ] = useState<number>(0);

    const [
        page,
        setPage
    ] = useState<number>(1);

    const [
        searchQuery,
        setSearchQuery
    ] = useState<string>("");

    const [
        debouncedSearchQuery,
        setDebouncedSearchQuery
    ] = useState<string>("");

    const [
        loading,
        setLoading
    ] = useState<boolean>(true);

    const [
        selectedMailId,
        setSelectedMailId
    ] = useState<string | null>(null);

    const [
        isDetailModalVisible,
        setIsDetailModalVisible
    ] = useState<boolean>(false);

    useEffect(() => {
        const timer = setTimeout(() => {
            setDebouncedSearchQuery(searchQuery);
        }, 500);

        return () => clearTimeout(timer);
    }, [
        searchQuery
    ]);

    const fetchMails = useCallback(async (currentPage: number, search: string) => {
        try {
            setLoading(true);
            const requestParams: {
                search?: string;
                limit: number;
                page: number;
            } = {
                page: currentPage,
                limit: 15
            };

            if(search.trim().length > 0) {
                requestParams.search = search.trim();
            }

            const response = await RESTServiceContext.action("getAllSentMails", requestParams);

            if(response?.payload) {
                setMails(response.payload.mails || []);
                setTotalCount(response.payload.totalCount || 0);
            } else {
                setMails([]);
                setTotalCount(0);
            }
        } catch(error) {
            console.error(error);
            setMails([]);
            setTotalCount(0);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMails(page, debouncedSearchQuery);
    }, [
        debouncedSearchQuery,
        fetchMails,
        page
    ]);

    const handleOpenDetail = (id: string) => {
        setSelectedMailId(id);
        setIsDetailModalVisible(true);
    };

    const handleCloseDetail = () => {
        setSelectedMailId(null);
        setIsDetailModalVisible(false);
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.headerContainer,
                {
                    marginBottom: spaces.spacingLg,
                    gap: spaces.spacingMd
                }
            ]}
        >
            <View
                style={stylesheet.searchContainer}
            >
                <TextInput
                    placeholder={localize("notifications-send-subject-placeholder")}
                    onChangeText={(val) => {
                        setSearchQuery(val);
                        setPage(1);
                    }}
                    value={searchQuery}
                />
            </View>
            <Button
                customTextColor={colors.project.brandWhite}
                title={localize("notifications-new-mail")}
                icon={({
                    size
                }) => <PlusIcon
                    color={colors.project.brandWhite}
                    size={size}
                />}
                customBorderColor="secondaryButton"
                customColor="secondaryButton"
                onPress={onOpenSendModal}
                spreadBehaviour="free"
                variant="outline"
            />
        </View>;
    };

    const renderTable = () => {
        if(loading) {
            return <View
                style={[
                    stylesheet.loadingContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        padding: spaces.spacingXl * 2,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <ActivityIndicator
                    color={colors.content.text.high}
                    size="large"
                />
            </View>;
        }

        if(mails.length === 0) {
            return <View
                style={[
                    stylesheet.emptyStateContainer,
                    {
                        backgroundColor: colors.project.brandWhite,
                        borderColor: colors.content.border.subtle,
                        borderWidth: borders.seperator,
                        padding: spaces.spacingXl * 2,
                        borderRadius: radiuses.md
                    }
                ]}
            >
                <Text
                    customColor={colors.content.text.mid}
                    variant="bodyMediumSize"
                >
                    {localize("notifications-mails-empty")}
                </Text>
            </View>;
        }

        return <View
            style={[
                stylesheet.tableContainer,
                {
                    backgroundColor: colors.project.brandWhite,
                    borderColor: colors.content.border.subtle,
                    borderWidth: borders.seperator,
                    borderRadius: radiuses.md
                }
            ]}
        >
            <View
                style={[
                    stylesheet.tableHeaderRow,
                    {
                        backgroundColor: colors.content.container.primary,
                        borderBottomColor: colors.content.border.subtle,
                        borderBottomWidth: borders.seperator,
                        paddingHorizontal: spaces.spacingLg,
                        paddingVertical: spaces.spacingMd
                    }
                ]}
            >
                <View
                    style={stylesheet.subjectCell}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-subject")}
                    </Text>
                </View>
                <View
                    style={stylesheet.targetCell}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-target")}
                    </Text>
                </View>
                <View
                    style={stylesheet.statusCell}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-status")}
                    </Text>
                </View>
                <View
                    style={stylesheet.dateCell}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-date")}
                    </Text>
                </View>
                <View
                    style={stylesheet.actionCell}
                >
                    <Text
                        customColor={colors.content.text.mid}
                        variant="bodySmallSize"
                    >
                        {localize("notifications-table-action")}
                    </Text>
                </View>
            </View>
            {mails.map((item) => {
                const isSuccess = item.status === "success";

                return <View
                    style={[
                        stylesheet.tableDataRow,
                        {
                            borderBottomColor: colors.content.border.subtle,
                            borderBottomWidth: borders.seperator,
                            paddingHorizontal: spaces.spacingLg,
                            paddingVertical: spaces.spacingMd
                        }
                    ]}
                    key={item._id}
                >
                    <View
                        style={stylesheet.subjectCell}
                    >
                        <Text
                            customColor={colors.content.text.high}
                            variant="bodyMediumSize"
                            numberOfLines={1}
                        >
                            {item.subject}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.targetCell}
                    >
                        <View
                            style={[
                                stylesheet.targetBadge,
                                {
                                    backgroundColor: colors.content.container.primary,
                                    paddingHorizontal: spaces.spacingSm,
                                    paddingVertical: spaces.spacingXs,
                                    borderRadius: radiuses.xs
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.high}
                                variant="bodySmallSize"
                            >
                                {item.sendToAll ? localize("notifications-table-all-members") : `${item.sentCount} ${localize("notifications-table-recipient-count")}`}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={stylesheet.statusCell}
                    >
                        <View
                            style={[
                                stylesheet.statusBadge,
                                {
                                    backgroundColor: colors.content.container.primary,
                                    paddingHorizontal: spaces.spacingSm,
                                    paddingVertical: spaces.spacingXs,
                                    borderRadius: radiuses.xs
                                }
                            ]}
                        >
                            <Text
                                customColor={colors.content.text.high}
                                variant="bodySmallSize"
                            >
                                {isSuccess ? localize("notifications-table-status-success") : item.status}
                            </Text>
                        </View>
                    </View>
                    <View
                        style={stylesheet.dateCell}
                    >
                        <Text
                            customColor={colors.content.text.mid}
                            variant="bodySmallSize"
                        >
                            {moment(item.createdAt).format("DD.MM.YYYY HH:mm")}
                        </Text>
                    </View>
                    <View
                        style={stylesheet.actionCell}
                    >
                        <Button
                            onPress={() => handleOpenDetail(item._id)}
                            icon={({
                                color,
                                size
                            }) => <EyeIcon
                                color={colors.content.icon[color]}
                                size={size}
                            />}
                            spreadBehaviour="free"
                            variant="ghost"
                            type="neutral"
                            size="small"
                        />
                    </View>
                </View>;
            })}
        </View>;
    };

    return <View
        style={stylesheet.container}
    >
        {renderHeader()}
        {renderTable()}
        {
            totalCount > 15 ?
                <View
                    style={[
                        stylesheet.paginationContainer,
                        {
                            marginTop: spaces.spacingLg
                        }
                    ]}
                >
                    <Pagination
                        onSelect={(item) => {
                            setPage(item.pageNumber);
                        }}
                        totalDataCount={totalCount}
                        currentPage={page}
                        itemPerPage={15}
                    />
                </View>
            :
                null
        }
        <MailDetailModal
            isVisible={isDetailModalVisible}
            onClose={handleCloseDetail}
            mailId={selectedMailId}
        />
    </View>;
};
export default MailsList;
